// TutorBubble — Claude-style streaming renderer for the Deskey Tutor.
//
// What it does (in order of how a turn is processed)
//   1. Sanitises the raw LLM text — normalises emphasis variants to
//      `**bold**` / `*italic*`, strips leftover backslash escapes ("\\|",
//      "\\_") and HTML chrome the model sometimes leaks.
//   2. Auto-wraps unwrapped math like `F=ma`, `v/t`, single Greek letters,
//      and superscripts so they render through KaTeX as real symbols.
//   3. Splits the text into a stream of structured blocks: headings,
//      paragraphs, bullet lists, numbered lists, fenced code, markdown
//      tables, and block math — with **bold** / *italic* / `code` / $math$
//      rendered inline. Tables work even when the model collapsed them onto
//      a single line (the case the user reported).
//   4. Reveals the answer line-by-line at ~220 char/sec with a soft
//      mechanical keyboard tick, like ChatGPT does.
//   5. Persists the mute preference + lazily creates the AudioContext on
//      first interaction to dodge browser autoplay policies.
//
// Renderer is intentionally token-stream over markdown-AST — we don't need
// a full markdown parser, and rolling our own keeps the bundle slim and
// the recovery from malformed model output graceful.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { AlertTriangle, BookOpen, Clock, Filter, Flag, GitMerge, Hash, Lightbulb, ListChecks, Quote, Scale, Zap } from 'lucide-react';
import { useStudent } from '../../StudentApp';
import { getExamModeColors } from '../../examPrepTheme';
import { splitExamReply } from './examReplyParts';
import { InlineSparkle } from '../../../../components/Loader';

// ─── Mute preference (shared across the app) ──────────────────────────────
const MUTE_KEY = 'desky_tutor_typing_muted';
export const isTypingMuted = () => {
  try { return localStorage.getItem(MUTE_KEY) === '1'; }
  catch { return false; }
};
export const setTypingMuted = (v) => {
  try { localStorage.setItem(MUTE_KEY, v ? '1' : '0'); }
  catch { /* ignore */ }
};

// ─── Soft mechanical keyboard tick — synthesised via Web Audio API ────────
let _audioCtx = null;
function getCtx() {
  if (_audioCtx) return _audioCtx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) _audioCtx = new Ctx();
  } catch { _audioCtx = null; }
  return _audioCtx;
}
function tick() {
  if (isTypingMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  const buffer = ctx.createBuffer(1, 600, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / 80);
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 2200 + Math.random() * 600;
  filter.Q.value = 0.9;
  const gain = ctx.createGain();
  gain.gain.value = 0.06;
  src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
  src.start(now);
  src.stop(now + 0.04);
}

// ─── Sanitisation ────────────────────────────────────────────────────────
// We strip artefacts BEFORE auto-wrapping math because a stray "\_" in the
// raw stream should disappear, not become "$\_$" (which KaTeX would render
// as an underscore literal).
function sanitize(s) {
  if (!s) return '';
  let out = s;
  // Remove HTML-ish chrome — backend bans it but it leaks occasionally.
  out = out.replace(/<\/?(p|div|span|br|hr|strong|em|b|i|u)[^>]*>/gi, '');
  // Normalise emphasis variants down to the two forms the inline renderer
  // understands: `**bold**` and `*italic*`. Triples first, then `__`.
  out = out
    .replace(/\*\*\*(.+?)\*\*\*/g, '**$1**')
    .replace(/__(.+?)__/g, '**$1**');
  // Stray "* " bullets → "- " bullets.
  out = out.replace(/^\s*\*\s+/gm, '- ');
  // Backslash escapes that aren't inside a math span: collapse them.
  // The model sometimes emits "\|" or "\_" thinking it's escaping markdown.
  // We do this OUTSIDE math regions — the math segmenter sees originals.
  out = stripStrayBackslashesOutsideMath(out);
  // Compress 3+ blank lines down to a single blank.
  out = out.replace(/\n{3,}/g, '\n\n');
  // Trim trailing spaces on every line — they make `<p>` lay out weirdly.
  out = out.replace(/[ \t]+$/gm, '');
  return out.trim();
}

// Replace `\|`, `\_`, `\(`, `\)`, `\{`, `\}`, `\[`, `\]` with their literal
// chars — UNLESS the segment is inside `$ ... $` or `$$ ... $$` (in which
// case KaTeX will interpret the escapes). We walk the string in spans.
function stripStrayBackslashesOutsideMath(s) {
  const re = /\$\$[\s\S]+?\$\$|\$[^$\n]+?\$/g;
  let last = 0;
  let out = '';
  let m;
  const swap = (chunk) => chunk
    .replace(/\\\|/g, '|')
    .replace(/\\_/g, '_')
    .replace(/\\\(/g, '(')
    .replace(/\\\)/g, ')')
    .replace(/\\\{/g, '{')
    .replace(/\\\}/g, '}')
    .replace(/\\\[/g, '[')
    .replace(/\\\]/g, ']');
  while ((m = re.exec(s)) !== null) {
    out += swap(s.slice(last, m.index));
    out += m[0];
    last = re.lastIndex;
  }
  out += swap(s.slice(last));
  return out;
}

// ─── Auto-wrap unwrapped math ────────────────────────────────────────────
// The backend prompt asks the model to wrap formulas in `$...$`, but Gemini
// sometimes writes `F = ma` or `v/t` as plain text. We do a conservative
// auto-wrap so the student sees real symbols. Heuristics — never touch text
// already inside `$...$`.
function autoWrapMath(s) {
  if (!s) return '';
  // Process only the spans OUTSIDE existing math.
  const re = /\$\$[\s\S]+?\$\$|\$[^$\n]+?\$/g;
  let last = 0;
  let out = '';
  let m;
  while ((m = re.exec(s)) !== null) {
    out += autoWrapPlain(s.slice(last, m.index));
    out += m[0];
    last = re.lastIndex;
  }
  out += autoWrapPlain(s.slice(last));
  return out;
}
function autoWrapPlain(s) {
  if (!s) return '';
  let out = s;
  // Simple equation patterns: `F = ma`, `a = v / t`, `E = mc^2`.
  // Match A = B form where both sides look like math (letters, numbers,
  // operators, exponents).
  out = out.replace(
    /(?<![\$\w])([a-zA-Z][a-zA-Z0-9]?(?:\^?\{[^}]+\}|\^[0-9])?)\s*=\s*([a-zA-Z0-9][a-zA-Z0-9\s\+\-\*\/\^\.\(\)]{0,30}?[a-zA-Z0-9\)])(?![\$\w])/g,
    (full, lhs, rhs) => `$${lhs} = ${rhs}$`,
  );
  // Lone Greek letter names — wrap so KaTeX renders the symbol.
  out = out.replace(
    /(?<![\$\\\w])(alpha|beta|gamma|delta|epsilon|theta|lambda|mu|pi|rho|sigma|tau|phi|psi|omega|Delta|Sigma|Pi|Omega)(?![\$\w])/g,
    (full, w) => `$\\${w}$`,
  );
  return out;
}

// ─── Block tokenisation ───────────────────────────────────────────────────
// Walks the sanitised text and emits a stream of typed blocks the renderer
// can map to React. Recognised block kinds:
//   { kind: 'heading', level: 1-6, text }
//   { kind: 'code',    lang, text }
//   { kind: 'table',   header: [cells], align: [...], rows: [[cells], ...] }
//   { kind: 'ul',      items: [text, ...] }
//   { kind: 'ol',      items: [text, ...] }
//   { kind: 'blockmath', text }
//   { kind: 'para',    text }
//   { kind: 'blank' }
function tokenize(text) {
  if (!text) return [];
  // Expand single-line table patterns into multiple lines so the
  // line-based parser below picks them up. The model sometimes collapses
  // a whole markdown table onto one line — split on `|` boundaries.
  text = expandInlineTables(text);
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code blocks
    const fence = line.match(/^\s*```(\w+)?\s*$/);
    if (fence) {
      const lang = fence[1] || '';
      const body = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++;  // consume closing fence
      blocks.push({ kind: 'code', lang, text: body.join('\n') });
      continue;
    }

    // Block math `$$...$$` spanning lines
    if (/^\s*\$\$/.test(line) && !/\$\$.*\$\$/.test(line)) {
      const body = [line.replace(/^\s*\$\$/, '')];
      i++;
      while (i < lines.length && !/\$\$\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      if (i < lines.length) {
        body.push(lines[i].replace(/\$\$\s*$/, ''));
        i++;
      }
      blocks.push({ kind: 'blockmath', text: body.join('\n').trim() });
      continue;
    }

    // Headings
    const heading = line.match(/^\s{0,3}(#{1,6})\s+(.+)\s*$/);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2].trim() });
      i++;
      continue;
    }

    // Blank line
    if (!line.trim()) {
      blocks.push({ kind: 'blank' });
      i++;
      continue;
    }

    // Table — header row + separator row + 1+ data rows
    if (isTableRow(line) && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      const header = splitRow(line);
      const align = parseAlign(lines[i + 1]);
      const rows = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ kind: 'table', header, align, rows });
      continue;
    }

    // Bullet list
    if (/^\s*[-•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-•]\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    // Numbered list — accept `1)` or `1.` as the prefix
    if (/^\s*\d+[\.\)]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+[\.\)]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+[\.\)]\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    // Default: greedy paragraph — accumulate non-empty lines until blank
    const buf = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*[-•]\s+/.test(lines[i]) &&
      !/^\s*\d+[\.\)]\s+/.test(lines[i]) &&
      !/^\s{0,3}#{1,6}\s+/.test(lines[i]) &&
      !/^\s*```/.test(lines[i]) &&
      !(isTableRow(lines[i]) && i + 1 < lines.length && isSeparatorRow(lines[i + 1]))
    ) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push({ kind: 'para', text: buf.join(' ') });
  }
  return blocks;
}

function isTableRow(line) {
  if (!line) return false;
  const trim = line.trim();
  return trim.startsWith('|') && trim.endsWith('|') && (trim.match(/\|/g) || []).length >= 2;
}
function isSeparatorRow(line) {
  if (!line) return false;
  const trim = line.trim();
  if (!trim.startsWith('|') || !trim.endsWith('|')) return false;
  const cells = trim.slice(1, -1).split('|').map(c => c.trim());
  return cells.length > 0 && cells.every(c => /^:?-{3,}:?$/.test(c) || /^:?-+:?$/.test(c));
}
function splitRow(line) {
  return line.trim().slice(1, -1).split('|').map(c => c.trim());
}
function parseAlign(line) {
  const cells = line.trim().slice(1, -1).split('|').map(c => c.trim());
  return cells.map(c => {
    const l = c.startsWith(':');
    const r = c.endsWith(':');
    if (l && r) return 'center';
    if (r) return 'right';
    return 'left';
  });
}

// When the model collapses a whole table onto one line like
// `| Law | Focus | | :-- | :-- | | 1st | foo | | 2nd | bar |`
// detect the run and reflow it back into multiple lines so the standard
// table parser above can do its job.
function expandInlineTables(text) {
  // A "row segment" is `| cell1 | cell2 | ... |` — possibly followed
  // immediately (no newline) by another row starting with `|`.
  // CRITICAL: the lookahead uses `[ \t]+` (NOT `\s*`) so a newline
  // BETWEEN two rows does NOT trigger insertion of another newline.
  // Without that guard we'd produce blank lines between every row and
  // break the table detector entirely.
  return text.replace(
    /(\|[^\n]+?\|)(?=[ \t]+\|[^\n]+?\|)/g,
    (match) => `${match}\n`,
  );
}

// ─── Inline rendering inside a paragraph / cell / list-item ───────────────
// Two-pass, recursion-free segmentation.
//   Pass 1 (segmentInlineBasic): math / code / text only.
//   Pass 2 (segmentInline): additionally peels `**bold**` and `*italic*`,
//     then runs pass 1 on the emphasis content — so `**$F=ma$**` or
//     `**a \`term\`**` still render their inner math/code pieces.
// Block math is hoisted by the block-level renderer.
function segmentInlineBasic(s) {
  if (!s) return [];
  const out = [];
  // Order matters: block-math `$$...$$` first, then inline code `\`...\``,
  // then inline math `$...$`.
  const re = /\$\$([\s\S]+?)\$\$|`([^`\n]+?)`|\$([^$\n]+?)\$/g;
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: s.slice(last, m.index) });
    if (m[1] != null) out.push({ kind: 'block-math', text: m[1].trim() });
    else if (m[2] != null) out.push({ kind: 'code', text: m[2] });
    else out.push({ kind: 'inline-math', text: m[3].trim() });
    last = re.lastIndex;
  }
  if (last < s.length) out.push({ kind: 'text', text: s.slice(last) });
  return out;
}

function segmentInline(s) {
  if (!s) return [];
  const out = [];
  // Math and code win over emphasis (so `$a*b$` stays math). The italic
  // pattern must hug its content — `3 * 4` stays literal.
  const re = /\$\$([\s\S]+?)\$\$|`([^`\n]+?)`|\$([^$\n]+?)\$|\*\*([^*\n](?:[^\n]*?[^*\n])?)\*\*|\*([^\s*][^*\n]*?[^\s*]|[^\s*])\*/g;
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: s.slice(last, m.index) });
    if (m[1] != null) out.push({ kind: 'block-math', text: m[1].trim() });
    else if (m[2] != null) out.push({ kind: 'code', text: m[2] });
    else if (m[3] != null) out.push({ kind: 'inline-math', text: m[3].trim() });
    else if (m[4] != null) out.push({ kind: 'bold', segs: segmentInlineBasic(m[4]) });
    else out.push({ kind: 'italic', segs: segmentInlineBasic(m[5]) });
    last = re.lastIndex;
  }
  if (last < s.length) out.push({ kind: 'text', text: s.slice(last) });
  return out;
}

// Renders one math / code / text leaf segment (no emphasis kinds).
function InlineLeaf({ seg, k }) {
  if (seg.kind === 'inline-math') {
    return <InlineMath key={k} math={seg.text} />;
  }
  if (seg.kind === 'block-math') {
    return (
      <span key={k} className="block my-1.5 overflow-x-auto text-center">
        <BlockMath math={seg.text} />
      </span>
    );
  }
  if (seg.kind === 'code') {
    return (
      <code
        key={k}
        className="px-1 py-px rounded text-xs font-mono"
        style={{ background: 'color-mix(in srgb, var(--st-ink) 8%, transparent)', color: 'var(--st-ink)' }}>
        {seg.text}
      </code>
    );
  }
  return <span key={k}>{seg.text.replace(/\s*\n\s*/g, ' ')}</span>;
}

function InlineSpan({ segs, keyPrefix }) {
  return (
    <>
      {segs.map((seg, j) => {
        if (seg.kind === 'bold') {
          return (
            <strong key={`${keyPrefix}-b-${j}`} className="font-semibold">
              {seg.segs.map((s2, j2) => (
                <InlineLeaf key={`${keyPrefix}-b-${j}-${j2}`} k={`${keyPrefix}-b-${j}-${j2}`} seg={s2} />
              ))}
            </strong>
          );
        }
        if (seg.kind === 'italic') {
          return (
            <em key={`${keyPrefix}-i-${j}`} className="italic">
              {seg.segs.map((s2, j2) => (
                <InlineLeaf key={`${keyPrefix}-i-${j}-${j2}`} k={`${keyPrefix}-i-${j}-${j2}`} seg={s2} />
              ))}
            </em>
          );
        }
        return <InlineLeaf key={`${keyPrefix}-s-${j}`} k={`${keyPrefix}-s-${j}`} seg={seg} />;
      })}
    </>
  );
}

// ─── Block renderer ───────────────────────────────────────────────────────
function renderBlock(b, idx) {
  if (b.kind === 'blank') {
    return <div key={idx} className="h-1.5" />;
  }
  if (b.kind === 'heading') {
    const tag = `h${Math.min(6, b.level)}`;
    const cls = b.level <= 2
      ? 'text-[15.5px] font-semibold mt-3 mb-1 leading-tight'
      : b.level === 3
        ? 'text-sm font-semibold mt-2.5 mb-1 leading-tight'
        : 'text-sm font-semibold mt-2 mb-0.5 leading-tight';
    return React.createElement(
      tag,
      { key: idx, className: cls },
      <InlineSpan keyPrefix={`h-${idx}`} segs={segmentInline(b.text)} />,
    );
  }
  if (b.kind === 'code') {
    return (
      <pre
        key={idx}
        className="my-2 rounded-lg px-3 py-2 overflow-x-auto text-xs leading-snug font-mono"
        style={{
          background: 'color-mix(in srgb, var(--st-ink) 6%, transparent)',
          border: '1px solid color-mix(in srgb, var(--st-ink) 12%, transparent)',
        }}>
        <code>{b.text}</code>
      </pre>
    );
  }
  if (b.kind === 'blockmath') {
    return (
      <div key={idx} className="my-2 overflow-x-auto text-center">
        <BlockMath math={b.text} />
      </div>
    );
  }
  if (b.kind === 'ul') {
    return (
      <ul key={idx} className="list-disc pl-5 space-y-1.5 my-2 marker:text-current/60">
        {b.items.map((it, j) => (
          <li key={j} className="leading-[1.65]">
            <InlineSpan keyPrefix={`ul-${idx}-${j}`} segs={segmentInline(it)} />
          </li>
        ))}
      </ul>
    );
  }
  if (b.kind === 'ol') {
    return (
      <ol key={idx} className="list-decimal pl-5 space-y-1.5 my-2 marker:font-bold">
        {b.items.map((it, j) => (
          <li key={j} className="leading-[1.65]">
            <InlineSpan keyPrefix={`ol-${idx}-${j}`} segs={segmentInline(it)} />
          </li>
        ))}
      </ol>
    );
  }
  if (b.kind === 'table') {
    return (
      <div key={idx} className="my-2 overflow-x-auto rounded-lg"
        style={{ border: '1px solid color-mix(in srgb, var(--st-ink) 12%, transparent)' }}>
        <table className="w-full text-xs border-collapse">
          <thead style={{ background: 'color-mix(in srgb, var(--st-primary) 8%, transparent)' }}>
            <tr>
              {b.header.map((cell, j) => (
                <th
                  key={j}
                  className="px-2.5 py-1.5 font-semibold text-left"
                  style={{ textAlign: b.align?.[j] || 'left' }}>
                  <InlineSpan keyPrefix={`th-${idx}-${j}`} segs={segmentInline(cell)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((row, r) => (
              <tr
                key={r}
                style={{
                  background: r % 2 === 0 ? 'transparent' : 'color-mix(in srgb, var(--st-ink) 3%, transparent)',
                  borderTop: '1px solid color-mix(in srgb, var(--st-ink) 8%, transparent)',
                }}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className="px-2.5 py-1.5 align-top"
                    style={{ textAlign: b.align?.[j] || 'left' }}>
                    <InlineSpan keyPrefix={`td-${idx}-${r}-${j}`} segs={segmentInline(cell)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  // paragraph
  return (
    <p key={idx} className="my-1.5 leading-[1.65]">
      <InlineSpan keyPrefix={`p-${idx}`} segs={segmentInline(b.text)} />
    </p>
  );
}

function renderRichText(rawText) {
  const cleaned = autoWrapMath(sanitize(rawText));
  const blocks = tokenize(cleaned);
  return blocks.map(renderBlock);
}

// ─── Exam Mode reply cards ────────────────────────────────────────────────
// The fixed lines Exam Mode ends every answer with, lifted out of the prose
// (examReplyParts.js) and shown as cards in Exam Mode's fixed gold palette. The
// trap goes last and loudest -- it's the line students should remember.
function ExamPartCard({ icon: Icon, label, children, colors }) {
  return (
    <div className="rounded-xl px-3 py-2.5 border flex gap-2.5"
      style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}>
      <Icon className="w-3.5 h-3.5 mt-[3px] flex-shrink-0" style={{ color: colors.gold }} />
      <div className="min-w-0 overflow-x-auto">
        <div className="text-xs font-semibold mb-0.5" style={{ color: colors.text }}>{label}</div>
        <div className="text-xs leading-[1.55]">{children}</div>
      </div>
    </div>
  );
}

// Every card an exam can produce, in display order (each exam only ever
// emits its own subset -- see backend chat_framework._EXAM_CARDS). Target
// time and Common trap are rendered separately below: a chip and the
// closing callout, on every exam.
const EXAM_CARDS = [
  { key: 'concepts', label: 'Concepts combined', icon: GitMerge },      // JEE Advanced
  { key: 'ncert', label: 'NCERT line', icon: BookOpen },               // NEET
  { key: 'exception', label: 'Exception to remember', icon: Flag },    // NEET
  { key: 'hook', label: 'Memory hook', icon: Lightbulb },              // NEET
  { key: 'contrast', label: 'Boards vs exam', icon: Scale },           // JEE Main
  { key: 'heuristic', label: 'Spot the pattern', icon: Zap },          // JEE Main, NEET calculations
  { key: 'eliminate', label: 'Options to eliminate', icon: Filter },   // NEET
  { key: 'options', label: 'Option check', icon: ListChecks },         // JEE Advanced
  { key: 'format', label: 'Answer format', icon: Hash },               // JEE Main numericals, SAT grid-in
  { key: 'evidence', label: 'Evidence', icon: Quote },                 // SAT Reading & Writing
];

function ExamReplyExtras({ parts }) {
  const colors = getExamModeColors(!!useStudent()?.theme?.dark);
  return (
    <div className="mt-3 space-y-2" data-testid="tutor-exam-extras">
      {EXAM_CARDS.filter(card => parts[card.key]).map(card => (
        <ExamPartCard key={card.key} icon={card.icon} label={card.label} colors={colors}>
          {renderRichText(parts[card.key])}
        </ExamPartCard>
      ))}
      {parts.time && (
        <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ background: colors.goldTint, color: colors.text }} data-testid="tutor-exam-time">
          <Clock className="w-3 h-3" /> Target time: {parts.time}
        </span>
      )}
      {parts.trap && (
        <div className="rounded-xl px-3 py-2.5 flex gap-2.5"
          style={{ background: colors.goldTint, borderLeft: `3px solid ${colors.gold}` }}
          data-testid="tutor-exam-trap">
          <AlertTriangle className="w-3.5 h-3.5 mt-[3px] flex-shrink-0" style={{ color: colors.text }} />
          <div className="min-w-0 overflow-x-auto">
            <div className="text-xs font-semibold mb-0.5" style={{ color: colors.text }}>Common trap</div>
            <div className="text-xs leading-[1.55]">{renderRichText(parts.trap)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Public component ─────────────────────────────────────────────────────
export default function AssistantBubble({ fullText, pending, onTypingDone, skipAnimation }) {
  // The animation works on a "reveal up to this many chars" basis using the
  // raw text. The block renderer re-tokenises every frame, which is fine —
  // the parser is fast and the bubble is at most ~2 kB of text.
  // Exam Mode replies: only the prose body types out; the fixed lines land
  // as cards once it finishes (a card half-typed would just flicker).
  const exam = useMemo(() => splitExamReply(fullText || ''), [fullText]);
  const sourceText = useMemo(() => (exam ? exam.body : (fullText || '')), [exam, fullText]);
  const [revealed, setRevealed] = useState(
    (skipAnimation || sourceText.length < 60) ? sourceText : ''
  );
  const lastFullRef = useRef(sourceText);
  const rafRef = useRef(null);
  const lastTickRef = useRef(0);

  // (Re)start the typewriter every time the underlying text changes.
  useEffect(() => {
    if (pending) return;
    lastFullRef.current = sourceText;
    if (skipAnimation || sourceText.length < 60) {
      setRevealed(sourceText);
      onTypingDone?.();
      return;
    }
    setRevealed('');
    let i = 0;
    let lastFrameTs = performance.now();
    const charsPerSecond = 220;
    const step = (ts) => {
      const dt = ts - lastFrameTs;
      lastFrameTs = ts;
      const advance = Math.max(2, Math.floor((charsPerSecond * dt) / 1000));
      i = Math.min(sourceText.length, i + advance);
      setRevealed(sourceText.slice(0, i));
      if (ts - lastTickRef.current > 60) {
        tick();
        lastTickRef.current = ts;
      }
      if (i < sourceText.length) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        onTypingDone?.();
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceText, pending, skipAnimation]);

  const rendered = useMemo(() => renderRichText(revealed), [revealed]);

  if (pending) {
    return (
      <div className="max-w-[85%] w-fit rounded-2xl px-3 py-2 text-xs flex items-center gap-2"
        style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}
        data-testid="tutor-bubble-pending">
        <InlineSparkle size={22} label="Tutor is thinking" />
        Thinking…
      </div>
    );
  }

  const stillTyping = revealed.length < sourceText.length;
  return (
    <div className="max-w-[92%] rounded-2xl px-4 py-3 text-sm leading-[1.65]"
      style={{
        background: 'var(--st-bg)',
        color: 'var(--st-ink)',
        fontFamily: 'var(--st-font-body, "Inter", system-ui, -apple-system, sans-serif)',
        letterSpacing: '0.005em',
      }}
      data-testid="tutor-bubble-ai">
      <div className="space-y-0">
        {rendered}
        {stillTyping && (
          <span aria-hidden="true"
            className="inline-block w-[1.5px] h-[1em] align-text-bottom ml-0.5 animate-pulse"
            style={{ background: 'var(--st-ink)' }} />
        )}
      </div>
      {exam && !stillTyping && <ExamReplyExtras parts={exam.parts} />}
    </div>
  );
}
